import { getTranslatedProperty } from "@shopware/helpers";
import type { Schemas } from "#shopware";

type JsonLDObject = { [key: string]: JsonLDValue };
type JsonLDValue =
    | string
    | number
    | boolean
    | null
    | undefined
    | JsonLDObject
    | JsonLDValue[];

interface Breadcrumb {
    name: string;
    path?: string;
}

export function useProductJsonLD(
    product: Schemas["Product"],
    options: {
        breadcrumbs?: Breadcrumb[];
    } = {},
): Record<string, never> {
    const { currencyCode } = usePrice();
    const requestUrl = useRequestURL();
    const route = useRoute();
    const baseUrl = requestUrl.origin;

    const firstSeoUrl = product.seoUrls?.[0];
    const productUrl =
        firstSeoUrl?.seoPathInfo
            ? `${baseUrl}/${firstSeoUrl.seoPathInfo}`
            : `${baseUrl}${route.fullPath}`;

    const description = getTranslatedProperty(product, "description").replace(
        /(<([^>]+)>)/gi,
        "",
    );

    const images = product.media?.length
        ? product.media
              .map((m: Schemas["ProductMedia"]) => m.media?.url)
              .filter(Boolean)
        : product.cover?.media?.url
          ? [product.cover.media.url]
          : [];

    const availability = product.available
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock";

    const productJsonLd: JsonLDObject = {
        "@context": "https://schema.org",
        "@type": "Product",
        name: getTranslatedProperty(product, "name"),
        description,
        sku: product.productNumber,
        image: images,
        url: productUrl,
        offers: {
            "@type": "Offer",
            url: productUrl,
            priceCurrency: currencyCode.value,
            price: product.calculatedPrice?.unitPrice ?? 0,
            availability,
            itemCondition: "https://schema.org/NewCondition",
        },
    };

    if (product.ean) {
        productJsonLd.gtin13 = product.ean;
    }

    if (product.manufacturer?.translated?.name) {
        productJsonLd.brand = {
            "@type": "Brand",
            name: product.manufacturer.translated.name,
        };
    }

    if (product.ratingAverage) {
        productJsonLd.aggregateRating = {
            "@type": "AggregateRating",
            ratingValue: product.ratingAverage,
            bestRating: 5,
            worstRating: 1,
        };
    }

    const scripts: Array<{ type: string; innerHTML: string }> = [
        {
            type: "application/ld+json",
            innerHTML: JSON.stringify(productJsonLd),
        },
    ];

    if (options.breadcrumbs?.length) {
        const breadcrumbJsonLd: JsonLDObject = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                {
                    "@type": "ListItem",
                    position: 1,
                    name: "Home",
                    item: baseUrl,
                },
                ...options.breadcrumbs.map(
                    (crumb: Breadcrumb, index: number) => ({
                        "@type": "ListItem" as const,
                        position: index + 2,
                        name: crumb.name,
                        ...(crumb.path
                            ? { item: `${baseUrl}${crumb.path}` }
                            : {}),
                    }),
                ),
            ],
        };
        scripts.push({
            type: "application/ld+json",
            innerHTML: JSON.stringify(breadcrumbJsonLd),
        });
    }

    useHead(() => ({
        script: scripts,
    }));

    return {};
}